/*
 * Copyright (c) Nexign, JSC, 2019-2021.
 */
/**
 *
 * @author Evgenij Tyan
 * @author Yakov Gushchin
 * @author Maxim Fedorov
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Julia Ignatieva
 * @version 1.12.0
 * @since 1.0.0
 */

import { createElement, ReactElement } from 'react';

import get from 'lodash/get';
import { createSelector } from 'reselect';
import { OutputSelector } from 'reselect/src';

import { IRootReducer } from '../../reducers/rootReducer';
import { IModalReducer } from './modalReducer';
import { EModalStatus, IModalList, IModalParams, IModalRenderProps } from './types/modalTypes';

const makeSelectModalState = (state: IRootReducer): IModalReducer => state.modal;

export const makeSelectorAllModals = (): OutputSelector<IRootReducer, IModalList,
    (state: IModalReducer) => IModalList> =>
    createSelector(
        makeSelectModalState,
        (state: IModalReducer) => state.modals,
    );

export const makeSelectorModalCurrent = (): OutputSelector<IRootReducer, string, (state: IModalReducer) => string> =>
    createSelector(
        makeSelectModalState,
        (state: IModalReducer) => state.currentModal,
    );

export const makeSelectorModalProps =
    (id?: string): OutputSelector<IRootReducer, IModalParams | undefined,
        (state: IModalReducer) => IModalParams | undefined> =>
        createSelector(
            makeSelectModalState,
            (state: IModalReducer) => get(state.modals[id || state.currentModal], 'modalParams'),
        );

export const makeSelectorModalIsOpen =
    (id?: string): OutputSelector<IRootReducer, boolean, (state: IModalReducer) => boolean> =>
        createSelector(
            makeSelectModalState,
            (state: IModalReducer) => get(state.modals[id || state.currentModal], 'isOpen', false),
        );

export const makeSelectorModalOnBackgroundCloseCallback =
    (id?: string): OutputSelector<IRootReducer, (() => void) | undefined,
        (state: IModalReducer) => (() => void) | undefined> =>
        createSelector(
            makeSelectModalState,
            (state: IModalReducer) => get(state.modals[id || state.currentModal], 'onBackgroundCloseCallback'),
        );

export const makeSelectorModalOnCloseCallback =
    (id?: string): OutputSelector<IRootReducer, (() => void) | undefined,
        (state: IModalReducer) => (() => void) | undefined> =>
        createSelector(
            makeSelectModalState,
            (state: IModalReducer) => get(state.modals[id || state.currentModal], 'onCloseCallback'),
        );

export const makeSelectorModalOnCrossClickCallback =
    (id?: string): OutputSelector<IRootReducer, (() => void) | undefined,
        (state: IModalReducer) => (() => void) | undefined> =>
        createSelector(
            makeSelectModalState,
            (state: IModalReducer) => get(state.modals[id || state.currentModal], 'onCrossClickCallback'),
        );

export const makeSelectorModalRenderComponent =
    (id?: string): OutputSelector<IRootReducer, (props: IModalRenderProps) => ReactElement,
        (state: IModalReducer) => (props: IModalRenderProps) => ReactElement> =>
        createSelector(
            makeSelectModalState,
            (state: IModalReducer) =>
                get(state.modals[id || state.currentModal], 'render', () => createElement('div')),
        );

export const makeSelectorModalStatus =
    (id?: string): OutputSelector<IRootReducer, EModalStatus, (state: IModalReducer) => EModalStatus> =>
        createSelector(
            makeSelectModalState,
            (state: IModalReducer) => get(state.modals[id || state.currentModal], 'status', EModalStatus.start),
        );
